import * as React from 'react';
import { forwardStyle, type ForwardStyleProps, styled, type StyledDecorator } from '@glitz/react';
import { type EmblaOptionsType, type EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { getComponentState, type MatchDevice, setComponentState, translate, useMatchDevice } from '@avensia/excite';
import { ArrowButton } from './CarouselArrow';
import { CarouselProgress } from './CarouselProgress';
import type { ThemeType } from 'webapp/theme';
import { createMinLargeViewportDecorator, createMinMediumViewportDecorator } from 'webapp/viewport';

type EmblaViewportRefType = <ViewportElement extends HTMLElement>(instance: ViewportElement | null) => void;

export type DeviceSettingType = { mobile: number; tablet: number; desktop: number };

type CarouselApiType = {
  emblaApi: EmblaCarouselType | undefined;
  emblaRef: EmblaViewportRefType;
  currentSlide: number;
  axis: 'x' | 'y';
};

type PropType = CarouselApiType & {
  visibleCount?: DeviceSettingType;
  useTotalCount?: boolean;
  gap?: number;
  autoFill?: boolean | string;
  options?: EmblaOptionsType;
  arrowNavigation?: boolean;
  arrowPosition?: number | string;
  progressBar?: boolean;
  progressDecorator?: StyledDecorator;
  containerDecorator?: StyledDecorator;
  showNextSlideFactor?: DeviceSettingType;
  buttonDecorator?: StyledDecorator;
  arrowTheme?: ThemeType;
};

export function useVisibleCount(visibleCount: DeviceSettingType) {
  const deviceMatch = useMatchDevice();
  return visibleItemsCount(deviceMatch, visibleCount);
}

function visibleItemsCount(deviceMatch: MatchDevice, visibleCount: DeviceSettingType) {
  switch (true) {
    case deviceMatch.isDesktop || deviceMatch.isLaptop:
      return visibleCount.desktop;
    case deviceMatch.isTablet:
      return visibleCount.tablet;
    default:
      return visibleCount.mobile;
  }
}

export function useCarousel(id?: string, options?: EmblaOptionsType) {
  const startIndex = id ? getComponentState<number>(id, 0) : (options?.startIndex ?? 0);
  const [currentSlide, setCurrentSlide] = React.useState(startIndex);

  //Lock startIndex in a ref to keep it from beeing recreated
  const startIndexRef = React.useRef<EmblaOptionsType>({
    startIndex,
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 'auto',
    startIndex,
    align: 'center',
    containScroll: 'keepSnaps',
    ...options,
    ...startIndexRef.current,
  });

  const onSelect = React.useCallback(() => {
    if (emblaApi) {
      setCurrentSlide(emblaApi.selectedScrollSnap());
      if (id) {
        setComponentState({ id, state: emblaApi.selectedScrollSnap() });
      }
    }
  }, [emblaApi, id]);

  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', onSelect);
      onSelect();
    }

    return () => {
      emblaApi?.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return {
    emblaApi,
    emblaRef,
    currentSlide,
    axis: options?.axis ? options.axis : 'x',
  };
}

export const Carousel = styled(
  forwardStyle(
    React.memo((props: React.PropsWithChildren<PropType> & ForwardStyleProps) => {
      const {
        visibleCount = { mobile: 1, tablet: 1, desktop: 1 },
        useTotalCount,
        gap = 0,
        autoFill,
        arrowNavigation = false,
        arrowPosition,
        progressBar = false,
        progressDecorator,
        containerDecorator,
        children,
        emblaRef,
        emblaApi,
        axis,
        compose,
        buttonDecorator,
        arrowTheme,
        showNextSlideFactor = { mobile: 1, tablet: 1, desktop: 1 },
      } = props;

      const [scrollProgress, setScrollProgress] = React.useState(0);

      const scrollPrev = React.useCallback(
        () => Boolean(emblaApi?.canScrollPrev()) && emblaApi?.scrollPrev(),
        [emblaApi],
      );
      const scrollNext = React.useCallback(
        () => Boolean(emblaApi?.canScrollNext()) && emblaApi?.scrollNext(),
        [emblaApi],
      );

      const gridAutoColumnsDecorator = React.useMemo(
        () =>
          styled(
            {
              gridAutoColumns: `calc(${100 / (visibleCount.mobile * showNextSlideFactor.mobile)}% - ${
                gap - gap / (visibleCount.mobile * showNextSlideFactor.mobile)
              }px)`,
            },
            createMinMediumViewportDecorator({
              gridAutoColumns: `calc(${100 / (visibleCount.tablet * showNextSlideFactor.tablet)}% - ${
                gap - gap / (visibleCount.tablet * showNextSlideFactor.tablet)
              }px)`,
            }),
            createMinLargeViewportDecorator({
              gridAutoColumns: `calc(${100 / (visibleCount.desktop * showNextSlideFactor.desktop)}% - ${
                gap - gap / (visibleCount.desktop * showNextSlideFactor.desktop)
              }px)`,
            }),
          ),
        [],
      );

      const gridDecorator = styled(
        typeof autoFill === 'boolean' || autoFill === undefined
          ? autoFill
            ? undefined
            : gridAutoColumnsDecorator
          : { gridAutoColumns: autoFill },
        {
          columnGap: gap,
          rowGap: gap,
        },
      );

      const onScroll = React.useCallback((emblaApi: EmblaCarouselType) => {
        const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()));
        setScrollProgress(progress * 100);
      }, []);

      React.useEffect(() => {
        if (!emblaApi) return;

        onScroll(emblaApi);
        emblaApi.on('reInit', onScroll);
        emblaApi.on('scroll', onScroll);
      }, [emblaApi, onScroll]);

      return (
        <>
          <CarouselWrapper css={compose()}>
            <CarouselViewport ref={emblaRef}>
              <CarouselContainer css={styled(gridDecorator, containerDecorator)}>{children}</CarouselContainer>
            </CarouselViewport>
            {arrowNavigation && (
              <>
                <ArrowButton
                  onClick={scrollPrev}
                  type={'prev'}
                  aria-label={translate(t => t.previous)}
                  enabled={Boolean(emblaApi?.canScrollPrev())}
                  position={arrowPosition}
                  as={arrowTheme}
                  decorator={buttonDecorator}
                />
                <ArrowButton
                  onClick={scrollNext}
                  type={'next'}
                  aria-label={translate(t => t.next)}
                  enabled={Boolean(emblaApi?.canScrollNext())}
                  position={arrowPosition}
                  as={arrowTheme}
                  decorator={buttonDecorator}
                />
              </>
            )}
          </CarouselWrapper>
          {progressBar && useTotalCount ? (
            <CarouselProgress css={progressDecorator} progress={scrollProgress} axis={axis} />
          ) : null}
        </>
      );
    }),
  ),
);

const CarouselWrapper = styled.div({
  maxWidth: '100%',
  position: 'relative',
});

const CarouselViewport = styled.div({
  overflow: 'hidden',
  padding: {
    xy: 0,
  },
});

const CarouselContainer = styled.div({
  display: 'grid',
  gridAutoFlow: 'column',
});
